import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Grid2,
  Tabs,
  Tab,
  Divider,
  Paper,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import DatePicker2 from "../../../../../views/components/common/textFields/DatePicker2";
import ImageUpload from "../../../../../views/components/common/upload/ImageUpload";
import dayjs, { Dayjs } from "dayjs";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { OrderType } from "../../../../../models/Order/Order";
import {
  ShipmentCreateOptions,
  ShipmentRequest,
} from "../../../../../models/AddShipment";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { SwagpacksStore } from "../../../stores/SwagpacksStore";
import {
  ShipmentType,
  parseCsvContent,
  validateCsvRows,
  removeEmptyRowsFromCsvText,
  createCsvFileFromText,
  validateEmail,
  validatePhoneDomestic,
  validatePostcodeDomestic,
} from "./csvShipmentUtils";

const swagpacksStore = new SwagpacksStore();

export const INVENTORY_SOURCE = {
  ORDER: "order",
  MASTER: "master",
} as const;

export type InventorySourceType =
  (typeof INVENTORY_SOURCE)[keyof typeof INVENTORY_SOURCE];

interface CreateShipmentWizardPopupProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  orderId?: number | null;
}

// Form data for single shipment
interface ShipmentFormData {
  first_name: string;
  last_name: string;
  email: string;
  alternate_email_id: string;
  phone: string;
  alternate_phone: string;
  address1: string;
  address2: string;
  landmark: string;
  country: string;
  state: string;
  city: string;
  pincode: string;
  dispatch_date: Dayjs | null;
}

const CopyFromCancelledToggle: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ checked, onChange }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 1 }}>
      <FormControlLabel
        labelPlacement="end"
        sx={{
          ml: 0,
          alignItems: "center",
          "& .MuiFormControlLabel-label": {
            fontSize: "0.8rem",
            fontWeight: 600,
          },
        }}
        control={
          <Checkbox
            checked={checked}
            size="small"
            onChange={(event) => onChange(event.target.checked)}
            sx={{
              p: 0.5,
              mr: 0.75,
              "&.Mui-checked": {
                color:
                  theme.palette.customColors?.darkBlue ||
                  theme.palette.primary.main,
              },
            }}
          />
        }
        label="Copy Details from Latest Cancelled Shipment"
      />
    </Box>
  );
};

const INVENTORY_SOURCE_LIST: FilterValue[] = [
  { id: INVENTORY_SOURCE.ORDER, value: "Order Inventory" },
  { id: INVENTORY_SOURCE.MASTER, value: "Master Inventory" },
];

const CreateShipmentWizardPopup: React.FC<CreateShipmentWizardPopupProps> =
  observer(({ open, onClose, onSuccess, orderId = null }) => {
    const theme = useTheme();

    const { customerStore, orderStore, shipmentStore, inventoryStore } =
      useStores();

    const [activeStep, setActiveStep] = useState(0);
    const [selectedCustomer, setSelectedCustomer] =
      useState<FilterValue | null>(null);
    const [inventorySource, setInventorySource] =
      useState<InventorySourceType | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<FilterValue | null>(
      null,
    );
    // const [selectedPacks, setSelectedPacks] = useState<FilterValue[]>([]);
    const [step1Errors, setStep1Errors] = useState<Record<string, string>>({});
    const [creationMode, setCreationMode] = useState<"single" | "csv">("csv");
    const [shipmentForm, setShipmentForm] = useState<ShipmentFormData>({
      first_name: "",
      last_name: "",
      email: "",
      alternate_email_id: "",
      phone: "",
      alternate_phone: "",
      address1: "",
      address2: "",
      landmark: "",
      country: "",
      state: "",
      city: "",
      pincode: "",
      dispatch_date: null,
    });
    const [shipmentErrors, setShipmentErrors] = useState<
      Record<string, string>
    >({});
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvValidationErrors, setCsvValidationErrors] = useState<
      { row: number; message: string }[]
    >([]);
    const [csvUploadResult, setCsvUploadResult] = useState<{
      success: number;
      failure: number;
      errors?: { row: number; message: string }[];
    } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inventoryFetchStatus, setInventoryFetchStatus] = useState<
      "idle" | "loading" | "found" | "not_found"
    >("idle");
    const [createShipmentError, setCreateShipmentError] = useState<
      string | null
    >(null);
    const [shipmentType, setShipmentType] = useState<ShipmentType | null>(null);
    const [csvUploadError, setCsvUploadError] = useState<string | null>(null);
    const [isResolvingInjectedOrder, setIsResolvingInjectedOrder] =
      useState(false);
    const [selectedOrderTypeId, setSelectedOrderTypeId] = useState<
      number | null
    >(null);
    const [orderTypeLoading, setOrderTypeLoading] = useState(false);
    const apiTimerRef = useRef<NodeJS.Timeout | null>(null);
    const orderTypeRequestRef = useRef(0);
    const shipmentOptionsRequestRef = useRef(0);
    const [shipmentOptions, setShipmentOptions] =
      useState<ShipmentCreateOptions | null>(null);
    const [shipmentOptionsLoading, setShipmentOptionsLoading] = useState(false);
    const [shipmentOptionsError, setShipmentOptionsError] = useState<
      string | null
    >(null);
    const [copyFromCancelled, setCopyFromCancelled] = useState(false);

    const isPodOrder = selectedOrderTypeId === OrderType.POD;
    const canCopyCancelledShipment = !!shipmentOptions?.has_cancelled_shipment;
    const podCreateBlocked =
      isPodOrder &&
      (shipmentOptionsLoading ||
        !!shipmentOptionsError ||
        !shipmentOptions?.has_shipments ||
        !shipmentOptions.can_recreate ||
        !canCopyCancelledShipment);

    const injectedOrderId =
      orderId != null && !Number.isNaN(Number(orderId)) && Number(orderId) > 0
        ? Number(orderId)
        : null;

    const resetWizardState = () => {
      setActiveStep(0);
      setSelectedCustomer(null);
      setInventorySource(null);
      setSelectedOrder(null);
      setStep1Errors({});
      setCreationMode("csv");
      setShipmentForm({
        first_name: "",
        last_name: "",
        email: "",
        alternate_email_id: "",
        phone: "",
        alternate_phone: "",
        address1: "",
        address2: "",
        landmark: "",
        country: "",
        state: "",
        city: "",
        pincode: "",
        dispatch_date: null,
      });
      setShipmentErrors({});
      setShipmentOptions(null);
      setShipmentOptionsLoading(false);
      setShipmentOptionsError(null);
      setCopyFromCancelled(false);
      shipmentOptionsRequestRef.current += 1;
      setCsvFile(null);
      setCsvValidationErrors([]);
      setCsvUploadResult(null);
      setInventoryFetchStatus("idle");
      setCreateShipmentError(null);
      setShipmentType(null);
      setCsvUploadError(null);
      setIsResolvingInjectedOrder(false);
      setSelectedOrderTypeId(null);
      setOrderTypeLoading(false);
      orderTypeRequestRef.current += 1;
      inventoryStore.orderInventoryId = null;
      inventoryStore.masterInventoryId = null;
    };

    const loadSelectedOrderType = async (orderId: number) => {
      const requestId = ++orderTypeRequestRef.current;
      setOrderTypeLoading(true);
      try {
        const cached = orderStore.orderBasicDetails;
        const details =
          cached && Number(cached.id) === orderId
            ? cached
            : await orderStore.orderAPI.getOrderBasicDetails(orderId);
        if (orderTypeRequestRef.current !== requestId) return;
        setSelectedOrderTypeId(
          details?.type_id != null ? Number(details.type_id) : null,
        );
      } catch (error) {
        console.error(error);
        if (orderTypeRequestRef.current !== requestId) return;
        setSelectedOrderTypeId(null);
      } finally {
        if (orderTypeRequestRef.current === requestId) {
          setOrderTypeLoading(false);
        }
      }
    };

    // Reset state when popup opens. If an order id is injected, resolve
    // inventory in step 1 and skip to step 2 when it is found.
    useEffect(() => {
      if (!open) {
        return;
      }

      let cancelled = false;
      resetWizardState();

      const hydrateFromOrderId = async (injectedId: number) => {
        setIsResolvingInjectedOrder(true);
        setInventorySource(INVENTORY_SOURCE.ORDER);
        setInventoryFetchStatus("loading");

        try {
          if (
            !orderStore.orderBasicDetails ||
            orderStore.orderBasicDetails.id !== injectedId
          ) {
            try {
              await orderStore.getOrderBasicDetails(injectedId);
            } catch (error) {
              console.error(error);
            }
          }

          if (cancelled) return;

          const details = orderStore.orderBasicDetails;
          const customerId = details?.customer?.id ?? details?.customer_id;
          const customerName =
            details?.customer?.name ?? details?.customer_name ?? "";
          const orderLabel = details?.label ?? "";

          if (customerId) {
            setSelectedCustomer({
              id: String(customerId),
              value: customerName,
            });
          }

          setSelectedOrder({
            id: String(injectedId),
            value: orderLabel
              ? `${injectedId} - ${orderLabel}`
              : String(injectedId),
          });
          void loadSelectedOrderType(injectedId);

          await inventoryStore.getOrderInventoryIdByOrderId(injectedId);
          if (cancelled) return;

          const resolvedInventoryId = inventoryStore.orderInventoryId;
          if (resolvedInventoryId != null) {
            setInventoryFetchStatus("found");
            setActiveStep(1);
          } else {
            setInventoryFetchStatus("not_found");
          }
        } catch (error) {
          console.error(error);
          if (cancelled) return;
          setInventoryFetchStatus("not_found");
        } finally {
          if (!cancelled) {
            setIsResolvingInjectedOrder(false);
          }
        }
      };

      if (injectedOrderId) {
        hydrateFromOrderId(injectedOrderId);
      }

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, injectedOrderId]);

    useEffect(() => {
      if (!open) return;
      const id = selectedOrder?.id ? Number(selectedOrder.id) : NaN;
      if (!Number.isInteger(id) || id <= 0) {
        setShipmentOptions(null);
        return;
      }

      let cancelled = false;
      const requestId = ++shipmentOptionsRequestRef.current;
      setShipmentOptionsLoading(true);
      setShipmentOptionsError(null);
      shipmentStore
        .getShipmentCreateOptions(id)
        .then((options) => {
          if (cancelled || shipmentOptionsRequestRef.current !== requestId) {
            return;
          }
          setShipmentOptions(options);
        })
        .catch((error: any) => {
          if (cancelled || shipmentOptionsRequestRef.current !== requestId) {
            return;
          }
          setShipmentOptions(null);
          setShipmentOptionsError(
            error?.response?.data?.message ||
              error?.message ||
              "Could not load shipment options.",
          );
        })
        .finally(() => {
          if (!cancelled && shipmentOptionsRequestRef.current === requestId) {
            setShipmentOptionsLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedOrder?.id]);

    // Clear pending debounce timer on unmount or when modal closes
    useEffect(() => {
      return () => {
        if (apiTimerRef.current) {
          clearTimeout(apiTimerRef.current);
          apiTimerRef.current = null;
        }
      };
    }, []);

    // // Packs: sorted by quantity (desc), name (asc)
    // const packListForDropdown: FilterValue[] = useMemo(
    //   () =>
    //     sortPacks(DUMMY_PACKS).map((p) => ({
    //       id: p.id,
    //       value: `${p.packName} (${p.clientSku}) - Qty: ${p.quantityAvailable}`,
    //     })),
    //   [],
    // );

    const selectedOrderIdDisplay = selectedOrder?.id ?? "null";
    const selectedInventoryIdDisplay =
      inventoryStore.orderInventoryId ??
      inventoryStore.masterInventoryId ??
      "null";

    const step1Valid = useMemo(() => {
      if (!selectedCustomer) return false;
      if (!inventorySource) return false;
      if (inventorySource === INVENTORY_SOURCE.ORDER && !selectedOrder)
        return false;
      if (
        inventoryStore.orderInventoryId === null &&
        inventoryStore.masterInventoryId === null
      )
        return false;

      return true;
    }, [
      selectedCustomer,
      inventorySource,
      selectedOrder,
      inventoryStore.orderInventoryId,
      inventoryStore.masterInventoryId,
    ]);

    const validateStep1 = (): boolean => {
      const errs: Record<string, string> = {};
      if (!selectedCustomer) errs.customer = "Customer is required";
      if (!inventorySource)
        errs.inventorySource = "Inventory source is required";
      if (inventorySource === INVENTORY_SOURCE.ORDER && !selectedOrder) {
        errs.order = "Order is required when Order Inventory is selected";
      }
      setStep1Errors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleNextStep = () => {
      if (activeStep === 0) {
        if (!validateStep1()) return;
      }
      setActiveStep((s) => s + 1);
    };

    const handleBackStep = () => {
      setActiveStep((s) => s - 1);
    };

    const validateSingleShipment = (): boolean => {
      const errs: Record<string, string> = {};
      if (!shipmentType) {
        errs._type = "Please select Domestic or International shipment type";
        setShipmentErrors(errs);
        return false;
      }
      if (!shipmentForm.first_name?.trim())
        errs.first_name = "First name is required";
      if (!shipmentForm.email?.trim()) errs.email = "Email is required";
      else if (!validateEmail(shipmentForm.email)) {
        errs.email = "Please enter a valid email";
      }
      if (!shipmentForm.phone?.trim()) errs.phone = "Phone is required";
      else if (
        shipmentType === "domestic" &&
        !validatePhoneDomestic(shipmentForm.phone)
      ) {
        errs.phone =
          "Contact No. must be 10 digits or 11 digits (starting with 0) for domestic";
      }
      if (!shipmentForm.address1?.trim()) errs.address1 = "Address is required";
      if (!shipmentForm.country?.trim()) errs.country = "Country is required";
      if (!shipmentForm.state?.trim()) errs.state = "State is required";
      if (!shipmentForm.city?.trim()) errs.city = "City is required";
      if (!shipmentForm.pincode?.trim()) errs.pincode = "Pin code is required";
      else if (
        shipmentType === "domestic" &&
        !validatePostcodeDomestic(shipmentForm.pincode)
      ) {
        errs.pincode = "Postcode must be exactly 6 digits for domestic";
      }
      setShipmentErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const handleSubmitSingle = () => {
      if (isPodOrder && podCreateBlocked) {
        return;
      }
      if (!copyFromCancelled && !validateSingleShipment()) {
        return;
      }
      if (!selectedCustomer) return;
      if (!inventorySource) return;
      if (inventorySource === INVENTORY_SOURCE.ORDER && !selectedOrder) return;
      // if (selectedPacks.length === 0) return;

      const inventoryId =
        inventoryStore.orderInventoryId ?? inventoryStore.masterInventoryId;

      if (!inventoryId) return;

      setIsSubmitting(true);
      setCreateShipmentError(null);

      const shipmentData: ShipmentRequest = {
        inventory_id: inventoryId,
        order_id: selectedOrder ? Number(selectedOrder.id) : null,
        pack_ids: null,
        first_name: shipmentForm.first_name,
        last_name: shipmentForm.last_name,
        email: shipmentForm.email,
        alternate_email_id: shipmentForm.alternate_email_id,
        phone: shipmentForm.phone,
        alternate_phone: shipmentForm.alternate_phone,
        address1: shipmentForm.address1,
        address2: shipmentForm.address2,
        landmark: shipmentForm.landmark,
        country: shipmentForm.country,
        state: shipmentForm.state,
        city: shipmentForm.city,
        pincode: shipmentForm.pincode,
        dispatch_date: shipmentForm.dispatch_date,
        copy_details: copyFromCancelled,
      };

      shipmentStore
        .createShipment(shipmentData)
        .then(() => {
          setCreateShipmentError(null);
          onSuccess?.();
          onClose();
        })
        .catch((error: any) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create shipment";
          setCreateShipmentError(message);
          console.error(error);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    };

    const handleCsvFileSelect = (files: File[]) => {
      if (!files?.length || !shipmentType) return;
      const file = files[0];
      if (!file.name.endsWith(".csv")) {
        setCsvValidationErrors([
          { row: 0, message: "Only CSV files are allowed" },
        ]);
        setCsvFile(null);
        setCsvUploadResult(null);
        setCsvUploadError(null);
        return;
      }
      setCsvUploadError(null);
      setCsvUploadResult(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = (e.target?.result as string) || "";
        const cleanedText = removeEmptyRowsFromCsvText(text);
        const rows = parseCsvContent(cleanedText);
        const errors = validateCsvRows(rows, shipmentType);
        const top5Errors = errors.slice(0, 5);

        setCsvValidationErrors(top5Errors);
        setCsvFile(
          top5Errors.length === 0
            ? createCsvFileFromText(cleanedText, file.name)
            : file,
        );
      };
      reader.readAsText(file);
    };

    const handleCsvRemove = () => {
      setCsvFile(null);
      setCsvValidationErrors([]);
      setCsvUploadResult(null);
      setCsvUploadError(null);
    };

    const handleCsvUpload = () => {
      setCsvUploadError(null);
      if (!shipmentType) {
        setCsvUploadError(
          "Please select Domestic or International shipment type",
        );
        return;
      }
      if (!csvFile) {
        setCsvValidationErrors([
          { row: 0, message: "Please select a CSV file" },
        ]);
        return;
      }
      if (csvValidationErrors.length > 0) {
        return;
      }

      const inventoryId =
        inventoryStore.orderInventoryId ?? inventoryStore.masterInventoryId;
      if (!inventoryId) {
        setCsvUploadError(
          "Inventory ID not found. Please go back to Step 1 and select Order Inventory.",
        );
        return;
      }

      setIsSubmitting(true);
      shipmentStore
        .createBulkShipmentsViaInventoryIdAndCsv(
          inventoryId,
          csvFile,
          selectedOrder ? Number(selectedOrder.id) : null,
        )
        .then((response: any) => {
          onSuccess?.();
          onClose();
        })
        .catch((error: any) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to create shipments from CSV";
          setCsvUploadError(message);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    };

    const handleCustomerSearch = (
      ignoreDelay: boolean = false,
      searchText?: string | undefined,
    ) => {
      try {
        if (apiTimerRef.current) {
          clearTimeout(apiTimerRef.current);
          apiTimerRef.current = null;
        }

        if (searchText && searchText.length < 3) {
          return;
        }

        const searchCustomers = () => {
          customerStore
            .searchCustomers({
              page_no: 1,
              search_text: searchText,
            })
            .catch((error) => {
              console.error(error);
            });
        };

        if (ignoreDelay) {
          searchCustomers();
          return;
        }

        apiTimerRef.current = setTimeout(() => {
          searchCustomers();
          apiTimerRef.current = null;
        }, 1000);
      } catch (error) {
        console.error(error);
      }
    };

    const handleOrderSearch = (
      ignoreDelay: boolean = false,
      customerId: number,
      searchText?: string | undefined,
    ) => {
      try {
        if (apiTimerRef.current) {
          clearTimeout(apiTimerRef.current);
          apiTimerRef.current = null;
        }

        if (searchText && searchText.length < 3) {
          return;
        }

        const searchOrders = () => {
          orderStore
            .searchOrderIdsAndLabels(customerId, searchText)
            .catch((error) => {
              console.error(error);
            });
        };

        if (ignoreDelay) {
          searchOrders();
          return;
        }

        apiTimerRef.current = setTimeout(() => {
          searchOrders();
          apiTimerRef.current = null;
        }, 1000);
      } catch (error) {
        console.error(error);
      }
    };

    // const handleSwagpackSearch = (
    //   ignoreDelay: boolean = false,
    //   inventoryId: number | null,
    //   searchText?: string | undefined,
    // ) => {
    //   if (!inventoryId) return;

    //   if (apiTimerRef.current) {
    //     clearTimeout(apiTimerRef.current);
    //     apiTimerRef.current = null;
    //   }

    //   if (searchText && searchText.length < 3) {
    //     return;
    //   }

    //   if (ignoreDelay) {
    //     swagpacksStore.getSwagpacks({
    //       inventory_id: inventoryId,
    //       search_text: searchText,
    //       page_no: 1,
    //       limit: 30,
    //       sort_by: "created_date",
    //       sort_type: SortType.DESC,
    //     });
    //     return;
    //   }

    //   apiTimerRef.current = setTimeout(() => {
    //     swagpacksStore.getSwagpacks({
    //       inventory_id: inventoryId,
    //       search_text: searchText,
    //       page_no: 1,
    //       limit: 30,
    //       sort_by: "created_date",
    //       sort_type: SortType.DESC,
    //     });
    //     apiTimerRef.current = null;
    //   }, 1000);
    // };

    const fetchOrderInventoryId = async (orderId: number) => {
      if (inventorySource !== INVENTORY_SOURCE.ORDER) return;
      setInventoryFetchStatus("loading");
      inventoryStore.orderInventoryId = null;
      try {
        await inventoryStore.getOrderInventoryIdByOrderId(orderId);
        const id = inventoryStore.orderInventoryId;
        setInventoryFetchStatus(id != null ? "found" : "not_found");
      } catch (error) {
        console.error(error);
        setInventoryFetchStatus("not_found");
      }
    };

    const fetchMasterInventoryId = async () => {
      if (!selectedCustomer) return;
      setInventoryFetchStatus("loading");
      inventoryStore.masterInventoryId = null;
      try {
        await inventoryStore.getMasterInventoryIdByCustomerId(
          Number(selectedCustomer.id),
        );
        const id = inventoryStore.masterInventoryId;
        setInventoryFetchStatus(id != null ? "found" : "not_found");
      } catch (error) {
        console.error(error);
        setInventoryFetchStatus("not_found");
      }
    };

    const handleCopyFromCancelled = (checked: boolean) => {
      setCopyFromCancelled(checked);
      setShipmentErrors({});
      if (!checked) {
        setShipmentForm({
          first_name: "",
          last_name: "",
          email: "",
          alternate_email_id: "",
          phone: "",
          alternate_phone: "",
          address1: "",
          address2: "",
          landmark: "",
          country: "",
          state: "",
          city: "",
          pincode: "",
          dispatch_date: null,
        });
        return;
      }
      const details = shipmentOptions?.cancelled_shipment;
      if (!details) return;
      setShipmentForm({
        first_name: details.first_name?.trim() ?? "",
        last_name: details.last_name?.trim() ?? "",
        email: details.email?.trim() ?? "",
        alternate_email_id: details.alternate_email_id?.trim() ?? "",
        phone: details.phone?.trim() ?? "",
        alternate_phone: details.alternate_phone?.trim() ?? "",
        address1: details.address1?.trim() ?? "",
        address2: details.address2?.trim() ?? "",
        landmark: details.landmark?.trim() ?? "",
        country: details.country?.trim() ?? "",
        state: details.state?.trim() ?? "",
        city: details.city?.trim() ?? "",
        pincode: details.pincode?.trim() ?? "",
        dispatch_date: details.dispatch_date
          ? dayjs(details.dispatch_date)
          : null,
      });
    };

    const handleInputChange = (field: keyof ShipmentFormData, value: any) => {
      setShipmentForm((prev) => ({ ...prev, [field]: value }));
      if (shipmentErrors[field]) {
        setShipmentErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

    const handleDownloadTemplate = async () => {
      const url =
        "https://procureasystorage.s3.ap-south-1.amazonaws.com/templates/CSV_Shipping_Template.csv";
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = "CSV_Shipping_Template.csv";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      } catch {
        window.open(url, "_blank");
      }
    };

    const steps = ["Customer & Inventory", "Shipment Creation"];

    const customColors = theme.palette.customColors as Record<string, string>;
    const greyBorder = customColors?.greyBorder || theme.palette.divider;
    const lightGreyColor =
      customColors?.lightGreyColor || theme.palette.grey[50];
    const greyTextColor =
      customColors?.greyTextColor || theme.palette.text.secondary;
    const blackBorder = customColors?.blackBorder || theme.palette.text.primary;
    const greyBackground =
      customColors?.greyBackground || theme.palette.grey[100];

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          transition: { timeout: 300, sx: { backdropFilter: "blur(4px)" } },
          backdrop: { timeout: 300, sx: { backdropFilter: "blur(4px)" } },
          paper: {
            sx: {
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              maxHeight: "90vh",
              margin: { xs: 2, sm: 4 },
              background: `linear-gradient(135deg, ${customColors?.white || "#fff"} 0%, ${lightGreyColor} 100%)`,
            },
          },
        }}
        sx={{
          zIndex: 2000,
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${customColors?.blueGradientStart || theme.palette.primary.main} 0%, ${customColors?.blueGradientEnd || theme.palette.primary.dark} 50%, ${customColors?.blueGradientStart || theme.palette.primary.main} 100%)`,
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
              padding: { xs: "12px 16px", sm: "16px 20px" },
              borderBottom: `1px solid ${greyBorder}`,
            }}
          >
            <Box>
              <CustomTypography
                variant="heading4"
                fontWeight={700}
                color={blackBorder}
              >
                Create Shipments
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={greyTextColor}
                sx={{ mt: 0.5 }}
              >
                Order or Master Inventory – 2 steps
              </CustomTypography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: greyTextColor,
                "&:hover": {
                  backgroundColor: lightGreyColor,
                  color: blackBorder,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Stepper
            activeStep={activeStep}
            sx={{
              flexShrink: 0,
              px: { xs: 1.5, sm: 3 },
              pt: 2,
              pb: 1,
              "& .MuiStepIcon-root.Mui-active": {
                color:
                  customColors?.blueGradientStart || theme.palette.primary.main,
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: customColors?.EmeraldGreen || theme.palette.success.main,
              },
              "& .MuiStepLabel-label.Mui-active": {
                fontWeight: 600,
                color: blackBorder,
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <DialogContent
            sx={{
              p: { xs: 1.5, sm: 3 },
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: greyBackground,
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  customColors?.greyDarkborder || theme.palette.grey[400],
                borderRadius: "4px",
                "&:hover": { backgroundColor: greyTextColor },
              },
            }}
          >
            {isResolvingInjectedOrder && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  minHeight: 220,
                }}
              >
                <CircularProgress size={28} />
                <CustomTypography variant="body2" color={greyTextColor}>
                  Resolving inventory for this order...
                </CustomTypography>
              </Box>
            )}

            {/* Step 1 */}
            {!isResolvingInjectedOrder && activeStep === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: "12px",
                  border: `1px solid ${greyBorder}`,
                  backgroundColor: "rgba(255,255,255,0.6)",
                }}
              >
                <CustomTypography
                  variant="heading6"
                  fontWeight={600}
                  sx={{ mb: 2 }}
                >
                  Customer & Inventory
                </CustomTypography>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  {/* Customer */}
                  <Box>
                    <CustomTypography
                      variant="body2"
                      fontWeight={500}
                      color={blackBorder}
                      sx={{ mb: 0.75 }}
                    >
                      Customer <span style={{ color: "#d32f2f" }}>*</span>
                    </CustomTypography>
                    <AppSearchDropdownMulti
                      minWidth="100%"
                      isMultiSelect={false}
                      title="Search customer"
                      value={selectedCustomer ? [selectedCustomer] : []}
                      list={customerStore.customers.map((c) => ({
                        id: c.id!.toString(),
                        value: c.name!,
                      }))}
                      local={false}
                      handleSearch={(searchText) =>
                        handleCustomerSearch(false, searchText)
                      }
                      handleSelect={(items: FilterValue[]) => {
                        setSelectedCustomer(items[0] || null);
                        setSelectedOrder(null);
                        setInventorySource(null);
                        // setSelectedPacks([]);
                        orderStore.orderIdsAndLabels = [];
                        swagpacksStore.swagpack = [];

                        inventoryStore.masterInventoryId = null;
                        inventoryStore.orderInventoryId = null;
                        setInventoryFetchStatus("idle");

                        if (step1Errors.customer)
                          setStep1Errors((p) => ({ ...p, customer: "" }));
                      }}
                      error={!!step1Errors.customer}
                      errorMessage={step1Errors.customer}
                      disabled={
                        !!injectedOrderId &&
                        inventoryFetchStatus !== "not_found"
                      }
                    />
                    {step1Errors.customer && (
                      <CustomTypography
                        variant="body2"
                        sx={{ color: "error.main", mt: 0.5 }}
                      >
                        {step1Errors.customer}
                      </CustomTypography>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: greyBorder }} />

                  {/* Inventory Source */}
                  <Box>
                    <CustomTypography
                      variant="body2"
                      fontWeight={500}
                      color={blackBorder}
                      sx={{ mb: 0.75 }}
                    >
                      Inventory Source{" "}
                      <span style={{ color: "#d32f2f" }}>*</span>
                    </CustomTypography>
                    <AppSearchDropdownMulti
                      minWidth="100%"
                      isMultiSelect={false}
                      title="Select inventory source"
                      value={
                        inventorySource
                          ? [
                              INVENTORY_SOURCE_LIST.find(
                                (i) => i.id === inventorySource,
                              )!,
                            ]
                          : []
                      }
                      list={INVENTORY_SOURCE_LIST}
                      local={true}
                      handleSelect={(items: FilterValue[]) => {
                        const val =
                          (items[0]?.id as InventorySourceType) || null;
                        setInventorySource(val);

                        setSelectedOrder(null);
                        // setSelectedPacks([]);
                        orderStore.orderIdsAndLabels = [];
                        swagpacksStore.swagpack = [];

                        inventoryStore.orderInventoryId = null;
                        inventoryStore.masterInventoryId = null;
                        setInventoryFetchStatus("idle");

                        if (val === INVENTORY_SOURCE.MASTER) {
                          fetchMasterInventoryId();
                        }

                        setStep1Errors((p) => ({
                          ...p,
                          inventorySource: "",
                          order: "",
                        }));
                      }}
                      error={!!step1Errors.inventorySource}
                      errorMessage={step1Errors.inventorySource}
                      disabled={
                        !!injectedOrderId &&
                        inventoryFetchStatus !== "not_found"
                      }
                    />
                    {step1Errors.inventorySource && (
                      <CustomTypography
                        variant="body2"
                        sx={{ color: "error.main", mt: 0.5 }}
                      >
                        {step1Errors.inventorySource}
                      </CustomTypography>
                    )}
                    {inventorySource === INVENTORY_SOURCE.MASTER &&
                      inventoryFetchStatus !== "idle" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {inventoryFetchStatus === "loading" && (
                            <CircularProgress size={16} />
                          )}
                          <CustomTypography
                            variant="body2"
                            color={
                              inventoryFetchStatus === "found"
                                ? customColors?.EmeraldGreen ||
                                  theme.palette.success.main
                                : inventoryFetchStatus === "not_found"
                                  ? theme.palette.error.main
                                  : greyTextColor
                            }
                          >
                            {inventoryFetchStatus === "loading" &&
                              "Checking inventory..."}
                            {inventoryFetchStatus === "found" &&
                              `Inventory id found - ${inventoryStore.masterInventoryId}`}
                            {inventoryFetchStatus === "not_found" &&
                              "No inventory found"}
                          </CustomTypography>
                        </Box>
                      )}
                  </Box>

                  {/* Order (when Order Inventory) */}
                  <Box>
                    <CustomTypography
                      variant="body2"
                      fontWeight={500}
                      color={blackBorder}
                      sx={{ mb: 0.75 }}
                    >
                      Order <span style={{ color: "#d32f2f" }}>*</span>
                    </CustomTypography>
                    <AppSearchDropdownMulti
                      minWidth="100%"
                      isMultiSelect={false}
                      title="Search order"
                      value={selectedOrder ? [selectedOrder] : []}
                      list={orderStore.orderIdsAndLabels.map((o) => ({
                        id: o.id.toString(),
                        value: `${o.id} - ${o.label}`,
                      }))}
                      local={false}
                      handleSearch={(searchText) => {
                        if (selectedCustomer) {
                          handleOrderSearch(
                            false,
                            Number(selectedCustomer.id),
                            searchText,
                          );
                        }
                      }}
                      handleSelect={(items: FilterValue[]) => {
                        inventoryStore.orderInventoryId = null;
                        inventoryStore.masterInventoryId = null;

                        const order = items[0] || null;
                        if (!order) return;
                        setSelectedOrder(order);
                        loadSelectedOrderType(Number(order.id));

                        if (inventorySource === INVENTORY_SOURCE.ORDER) {
                          fetchOrderInventoryId(Number(order.id));
                        }

                        if (step1Errors.order)
                          setStep1Errors((p) => ({ ...p, order: "" }));
                      }}
                      error={!!step1Errors.order}
                      errorMessage={step1Errors.order}
                      disabled={
                        (!!injectedOrderId &&
                          inventoryFetchStatus !== "not_found") ||
                        inventorySource !== INVENTORY_SOURCE.ORDER ||
                        !selectedCustomer
                      }
                    />

                    {step1Errors.order && (
                      <CustomTypography
                        variant="body2"
                        sx={{ color: "error.main", mt: 0.5 }}
                      >
                        {step1Errors.order}
                      </CustomTypography>
                    )}
                    {inventorySource === INVENTORY_SOURCE.ORDER &&
                      inventoryFetchStatus !== "idle" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {inventoryFetchStatus === "loading" && (
                            <CircularProgress size={16} />
                          )}
                          <CustomTypography
                            variant="body2"
                            color={
                              inventoryFetchStatus === "found"
                                ? customColors?.EmeraldGreen ||
                                  theme.palette.success.main
                                : inventoryFetchStatus === "not_found"
                                  ? theme.palette.error.main
                                  : greyTextColor
                            }
                          >
                            {inventoryFetchStatus === "loading" &&
                              "Checking inventory..."}
                            {inventoryFetchStatus === "found" &&
                              `Inventory id found - ${inventoryStore.orderInventoryId}`}
                            {inventoryFetchStatus === "not_found" &&
                              "No inventory found"}
                          </CustomTypography>
                        </Box>
                      )}
                  </Box>

                  {/* <Divider sx={{ borderColor: greyBorder }} /> */}
                  {/* <Box>
                    <CustomTypography
                      variant="body2"
                      fontWeight={500}
                      color={blackBorder}
                      sx={{ mb: 0.5 }}
                    >
                      Pack Selection{" "}
                      <span style={{ color: greyTextColor, fontWeight: 400 }}>
                        (Optional)
                      </span>
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      color={greyTextColor}
                      sx={{ mb: 1, fontSize: "0.8rem" }}
                    >
                      Select packs to include. You can also add packs in Step 2.
                    </CustomTypography>
                    <AppSearchDropdownMulti
                      title="Search by pack name or client SKU"
                      list={swagpacksStore.swagpack.map((p) => ({
                        id: p.id.toString(),
                        value: `${p.id} - ${p.label ?? ""} - (${
                          (p.quantity ?? 0) - (p.used_quantity ?? 0)
                        })`,
                      }))}
                      value={selectedPacks}
                      handleSearch={(searchText) =>
                        selectedCustomer
                          ? handleSwagpackSearch(
                              false,
                              inventoryStore.orderInventoryId ??
                                inventoryStore.masterInventoryId,
                              searchText,
                            )
                          : undefined
                      }
                      local={true}
                      handleSelect={setSelectedPacks}
                      minWidth="100%"
                    />
                    {selectedPacks.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          mt: 1,
                          p: 1.5,
                          borderRadius: "8px",
                          backgroundColor: lightGreyColor,
                          border: `1px solid ${greyBorder}`,
                        }}
                      >
                        {selectedPacks.map((p) => (
                          <Chip
                            key={p.id}
                            label={p.value}
                            size="small"
                            onDelete={() =>
                              setSelectedPacks((prev) =>
                                prev.filter(
                                  (x) => String(x.id) !== String(p.id),
                                ),
                              )
                            }
                            sx={{
                              backgroundColor: theme.palette.background.paper,
                              fontWeight: 500,
                              "& .MuiChip-deleteIcon": { fontSize: "0.9rem" },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    {packListForDropdown.length === 0 && (
                      <CustomTypography
                        variant="body2"
                        color={greyTextColor}
                        sx={{ mt: 1 }}
                      >
                        No packs available for this inventory source.
                      </CustomTypography>
                    )}
                  </Box> */}
                </Box>
              </Paper>
            )}

            {/* Step 2 */}
            {!isResolvingInjectedOrder && activeStep === 1 && (
              <Box>
                <Grid2 container spacing={2} sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        border: `1px solid ${greyBorder}`,
                        backgroundColor: lightGreyColor,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color={greyTextColor}
                        sx={{ mb: 0.25 }}
                      >
                        Order ID
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        color={blackBorder}
                      >
                        {selectedOrderIdDisplay}
                      </CustomTypography>
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "8px",
                        border: `1px solid ${greyBorder}`,
                        backgroundColor: lightGreyColor,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color={greyTextColor}
                        sx={{ mb: 0.25 }}
                      >
                        Inventory ID
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        color={blackBorder}
                      >
                        {selectedInventoryIdDisplay}
                      </CustomTypography>
                    </Box>
                  </Grid2>
                </Grid2>

                {orderTypeLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : podCreateBlocked ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: "12px",
                      border: `1px solid ${greyBorder}`,
                      backgroundColor: "rgba(255,255,255,0.6)",
                    }}
                  >
                    {shipmentOptionsLoading ? (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : shipmentOptionsError ? (
                      <CustomTypography variant="body2" color="error.main">
                        {shipmentOptionsError}
                      </CustomTypography>
                      ) : !shipmentOptions?.has_shipments ? (
                        <CustomTypography variant="body2" color={greyTextColor}>
                          This POD order has no shipment to create again.
                        </CustomTypography>
                      ) : !shipmentOptions.can_recreate ? (
                        <CustomTypography variant="body2" color={greyTextColor}>
                          This POD order already has its open shipments. Cancel a
                          shipment before creating it again.
                        </CustomTypography>
                    ) : (
                      <CustomTypography variant="body2" color={greyTextColor}>
                        There is no cancelled shipment to copy for this POD order.
                      </CustomTypography>
                    )}
                    {createShipmentError && (
                      <CustomTypography
                        variant="body2"
                        color="error.main"
                        sx={{ mt: 1.5 }}
                      >
                        {createShipmentError}
                      </CustomTypography>
                    )}
                  </Paper>
                ) : (
                  <>
                <FormControl component="fieldset" sx={{ mb: 1 }}>
                  <FormLabel
                    component="legend"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: blackBorder,
                      mb: 0.5,
                    }}
                  >
                    Is this a domestic or international shipment?
                  </FormLabel>
                  <RadioGroup
                    row
                    value={shipmentType ?? ""}
                    onChange={(e) => {
                      const val = e.target.value as ShipmentType;
                      setShipmentType(val);
                      setCsvFile(null);
                      setCsvValidationErrors([]);
                      setCsvUploadResult(null);
                      setCsvUploadError(null);
                    }}
                  >
                    <FormControlLabel
                      value="domestic"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.8rem",
                        },
                      }}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: "0.8rem" },
                            "&.Mui-checked": {
                              color:
                                customColors?.blueGradientStart ||
                                theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label="Domestic"
                    />
                    <FormControlLabel
                      value="international"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.8rem",
                        },
                      }}
                      control={
                        <Radio
                          sx={{
                            "& .MuiSvgIcon-root": { fontSize: "0.8rem" },
                            "&.Mui-checked": {
                              color:
                                customColors?.blueGradientStart ||
                                theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label="International"
                    />
                  </RadioGroup>
                </FormControl>


                {shipmentType && !isPodOrder ? (
                  <Tabs
                    value={creationMode}
                    onChange={(_, v: "single" | "csv") => setCreationMode(v)}
                    sx={{
                      mb: 2,
                      minHeight: 44,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        py: { xs: 1, sm: 1.5 },
                      },
                      "& .Mui-selected": {
                        color:
                          customColors?.blueGradientStart ||
                          theme.palette.primary.main,
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor:
                          customColors?.blueGradientStart ||
                          theme.palette.primary.main,
                        height: 3,
                      },
                    }}
                  >
                    <Tab label="CSV Upload (Bulk)" value="csv" />
                    <Tab label="Single Shipment" value="single" />
                  </Tabs>
                ) : null}

                {(isPodOrder || creationMode === "single") && shipmentType ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: "12px",
                      border: `1px solid ${greyBorder}`,
                      backgroundColor: "rgba(255,255,255,0.6)",
                    }}
                  >
                    <>
                      {canCopyCancelledShipment ? (
                        <CopyFromCancelledToggle
                          checked={copyFromCancelled}
                          onChange={handleCopyFromCancelled}
                        />
                      ) : null}
                      <CustomTypography
                        variant="body2"
                        color={greyTextColor}
                        sx={{ mb: 2 }}
                      >
                        Enter recipient and shipping details. Packs can be
                        selected here if not chosen in Step 1.
                      </CustomTypography>
                      {isPodOrder &&
                      shipmentOptions?.both_shipments_will_be_created ? (
                        <CustomTypography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            mb: 2,
                            "@keyframes podShipmentNoticeBlink": {
                              "0%, 49%": { opacity: 1 },
                              "50%, 100%": { opacity: 0.15 },
                            },
                            animation:
                              "podShipmentNoticeBlink 1s step-start infinite",
                            "@media (prefers-reduced-motion: reduce)": {
                              animation: "none",
                            },
                          }}
                        >
                          Both cancelled shipments (POD Shipment and inventory Shipment)
                          will be created for the Order.
                        </CustomTypography>
                      ) : null}
                      <Grid2 size={12} sx={{ mb: 1 }}>
                        <CustomTypography variant="heading6" fontWeight={600}>
                          Personal Information
                        </CustomTypography>
                      </Grid2>
                      <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <CustomTextField
                            placeholder="First Name *"
                            value={shipmentForm.first_name}
                            onSearch={(v) => handleInputChange("first_name", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.first_name}
                          />
                          {shipmentErrors.first_name && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.first_name}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <CustomTextField
                            placeholder="Last Name"
                            value={shipmentForm.last_name}
                            onSearch={(v) => handleInputChange("last_name", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                          />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <CustomTextField
                            placeholder="Email *"
                            value={shipmentForm.email}
                            onSearch={(v) => handleInputChange("email", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.email}
                          />
                          {shipmentErrors.email && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.email}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <CustomTextField
                            placeholder="Phone *"
                            value={shipmentForm.phone}
                            onSearch={(v) => handleInputChange("phone", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.phone}
                          />
                          {shipmentErrors.phone && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.phone}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={12} sx={{ mt: 1 }}>
                          <CustomTypography variant="heading6" fontWeight={600}>
                            Shipping Address
                          </CustomTypography>
                        </Grid2>
                        <Grid2 size={12}>
                          <CustomTextField
                            placeholder="Address 1 *"
                            value={shipmentForm.address1}
                            onSearch={(v) => handleInputChange("address1", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.address1}
                          />
                          {shipmentErrors.address1 && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.address1}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <CustomTextField
                            placeholder="Address 2"
                            value={shipmentForm.address2}
                            onSearch={(v) => handleInputChange("address2", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.address2}
                          />
                          {shipmentErrors.address2 && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.address2}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <CustomTextField
                            placeholder="Landmark"
                            value={shipmentForm.landmark}
                            onSearch={(v) => handleInputChange("landmark", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.landmark}
                          />
                          {shipmentErrors.landmark && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.landmark}
                            </CustomTypography>
                          )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 4 }}>
                          <CustomTextField
                            placeholder="Country *"
                            value={shipmentForm.country}
                            onSearch={(v) => handleInputChange("country", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.country}
                          />
                          {shipmentErrors.country && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.country}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                          <CustomTextField
                            placeholder="State *"
                            value={shipmentForm.state}
                            onSearch={(v) => handleInputChange("state", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.state}
                          />
                          {shipmentErrors.state && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.state}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                          <CustomTextField
                            placeholder="City *"
                            value={shipmentForm.city}
                            onSearch={(v) => handleInputChange("city", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.city}
                          />
                          {shipmentErrors.city && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.city}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <CustomTextField
                            placeholder="Pin Code *"
                            value={shipmentForm.pincode}
                            onSearch={(v) => handleInputChange("pincode", v)}
                            width="100%"
                            disabled={copyFromCancelled}
                            error={!!shipmentErrors.pincode}
                          />
                          {shipmentErrors.pincode && (
                            <CustomTypography
                              variant="body2"
                              sx={{ color: "error.main", mt: 0.5 }}
                            >
                              {shipmentErrors.pincode}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <DatePicker2
                            type="date"
                            label="Dispatch Date"
                            value={shipmentForm.dispatch_date}
                            disabled={copyFromCancelled}
                            onChange={(d) =>
                              handleInputChange("dispatch_date", d)
                            }
                          />
                        </Grid2>
                      </Grid2>
                      {createShipmentError && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: "8px",
                            backgroundColor: `${theme.palette.error.main}10`,
                            border: `1px solid ${theme.palette.error.main}30`,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            color="error.main"
                            fontWeight={500}
                          >
                            {createShipmentError}
                          </CustomTypography>
                        </Box>
                      )}
                      {shipmentErrors._type && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: "8px",
                            backgroundColor: `${theme.palette.error.main}10`,
                            border: `1px solid ${theme.palette.error.main}30`,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            color="error.main"
                            fontWeight={500}
                          >
                            {shipmentErrors._type}
                          </CustomTypography>
                        </Box>
                      )}
                    </>
                  </Paper>
                ) : null}

                {!isPodOrder && creationMode === "csv" && shipmentType ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2.5 },
                      borderRadius: "12px",
                      border: `1px solid ${greyBorder}`,
                      backgroundColor: "rgba(255,255,255,0.6)",
                    }}
                  >
                    <Grid2 container spacing={2}>
                      <Grid2 size={{ xs: 12, md: 8 }}>
                        <CustomTypography
                          variant="body2"
                          color={greyTextColor}
                          sx={{ mb: 2 }}
                        >
                          Required columns: S.No., First Name, Contact No.,
                          Email Id, Address Line 1, Address Country, Address
                          State, Address City, Address Postcode.
                        </CustomTypography>
                      </Grid2>
                      <Grid2
                        size={{ xs: 12, md: 4 }}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <AppButton
                          variant="primaryBlock"
                          label="Download Template"
                          onClick={handleDownloadTemplate}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        <ImageUpload
                          key={shipmentType}
                          size="large"
                          showImagesList={false}
                          uploadText="Drop CSV file here or click to browse"
                          addImagesToBeUploaded={handleCsvFileSelect}
                          removeImage={handleCsvRemove}
                          allowedFileTypes=".csv"
                          fileInfo="Maximum file size: 10MB"
                          disabled={false}
                        />
                      </Grid2>
                      <Grid2 size={12}>
                        {csvFile && (
                          <CustomTypography
                            variant="body2"
                            sx={{ mt: 1, color: "success.main" }}
                          >
                            ✓ {csvFile.name}
                          </CustomTypography>
                        )}
                        {csvValidationErrors.length > 0 && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              borderRadius: "8px",
                              backgroundColor: `${theme.palette.error.main}10`,
                              border: `1px solid ${theme.palette.error.main}30`,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              fontWeight={600}
                              color="error.main"
                              sx={{ mb: 1 }}
                            >
                              Top 5 validation errors
                            </CustomTypography>
                            {csvValidationErrors.map((e, i) => (
                              <CustomTypography
                                key={i}
                                variant="body2"
                                color="error.dark"
                              >
                                Row {e.row}: {e.message}
                              </CustomTypography>
                            ))}
                          </Box>
                        )}
                        {csvUploadError && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              borderRadius: "8px",
                              backgroundColor: `${theme.palette.error.main}10`,
                              border: `1px solid ${theme.palette.error.main}30`,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              fontWeight={600}
                              color="error.main"
                            >
                              {csvUploadError}
                            </CustomTypography>
                          </Box>
                        )}
                        {csvUploadResult && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              borderRadius: "8px",
                              border: `1px solid ${greyBorder}`,
                              backgroundColor: lightGreyColor,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              fontWeight={600}
                              color={blackBorder}
                              sx={{ mb: 1 }}
                            >
                              Upload Summary
                            </CustomTypography>
                            <CustomTypography
                              variant="body2"
                              color="success.main"
                            >
                              Success: {csvUploadResult.success} records
                            </CustomTypography>
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                            >
                              Failed: {csvUploadResult.failure} records
                            </CustomTypography>
                            {csvUploadResult.errors &&
                              csvUploadResult.errors.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <CustomTypography
                                    variant="body2"
                                    fontWeight={600}
                                    color="error.main"
                                    sx={{ mb: 0.5 }}
                                  >
                                    Errors:
                                  </CustomTypography>
                                  {csvUploadResult.errors.map((e, i) => (
                                    <CustomTypography
                                      key={i}
                                      variant="body2"
                                      color="error.dark"
                                    >
                                      Row {e.row}: {e.message}
                                    </CustomTypography>
                                  ))}
                                </Box>
                              )}
                          </Box>
                        )}
                      </Grid2>
                    </Grid2>
                  </Paper>
                ) : null}
                  </>
                )}
              </Box>
            )}
          </DialogContent>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
              px: { xs: 1.5, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              borderTop: `1px solid ${greyBorder}`,
              backgroundColor: lightGreyColor,
            }}
          >
            <AppButton
              variant="grayscale"
              label="Back"
              onClick={handleBackStep}
              disabled={activeStep === 0 || isResolvingInjectedOrder}
              customStyles={{
                borderColor:
                  customColors?.greyDarkborder || theme.palette.grey[400],
                color: greyTextColor,
                "&:hover": {
                  borderColor: greyTextColor,
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
            <Box
              sx={{ display: "flex", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}
            >
              {activeStep === 0 ? (
                <AppButton
                  variant="primary"
                  label="Next"
                  onClick={handleNextStep}
                  disabled={!step1Valid || isResolvingInjectedOrder}
                  customStyles={{
                    backgroundColor:
                      customColors?.blueGradientStart ||
                      theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor:
                        customColors?.blueGradientHoverStart ||
                        theme.palette.primary.dark,
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 12px ${customColors?.blueGradientStart || theme.palette.primary.main}30`,
                    },
                  }}
                />
              ) : isPodOrder || creationMode === "single" ? (
                <AppButton
                  variant="primary"
                  label={isSubmitting ? "Creating..." : "Create Shipment"}
                  onClick={handleSubmitSingle}
                  disabled={
                    isSubmitting ||
                    orderTypeLoading ||
                    podCreateBlocked ||
                    (isPodOrder && !shipmentType)
                  }
                  customStyles={{
                    backgroundColor:
                      customColors?.EmeraldGreen || theme.palette.success.main,
                    "&:hover": {
                      backgroundColor:
                        customColors?.greenDark || theme.palette.success.dark,
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 12px ${customColors?.EmeraldGreen || theme.palette.success.main}30`,
                    },
                  }}
                />
              ) : (
                <AppButton
                  variant="primary"
                  label={isSubmitting ? "Creating..." : "Create Shipments"}
                  onClick={handleCsvUpload}
                  disabled={
                    !shipmentType ||
                    !csvFile ||
                    csvValidationErrors.length > 0 ||
                    isSubmitting
                  }
                  customStyles={{
                    backgroundColor:
                      customColors?.EmeraldGreen || theme.palette.success.main,
                    "&:hover": {
                      backgroundColor:
                        customColors?.greenDark || theme.palette.success.dark,
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 12px ${customColors?.EmeraldGreen || theme.palette.success.main}30`,
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Dialog>
    );
  });

export default CreateShipmentWizardPopup;
